import { useCallback, useSyncExternalStore } from 'react';
import {
	AlertFrequency,
	AlertType,
	FundingRateAlert,
	MAX_FUNDING_RATE_ALERTS,
} from '../types';

const STORAGE_KEY = 'fundingRateAlerts';

let listeners: Array<() => void> = [];

function emitChange() {
	for (const listener of listeners) {
		listener();
	}
}

function getAlerts(): FundingRateAlert[] {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch {
		// ignore
	}
	return [];
}

let cachedSnapshot: FundingRateAlert[] = getAlerts();

function saveAlerts(alerts: FundingRateAlert[]) {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
	cachedSnapshot = alerts;
	emitChange();
}

function subscribe(listener: () => void) {
	listeners = [...listeners, listener];
	return () => {
		listeners = listeners.filter((l) => l !== listener);
	};
}

function getSnapshot(): FundingRateAlert[] {
	return cachedSnapshot;
}

function getServerSnapshot(): FundingRateAlert[] {
	return [];
}

export function useFundingRateAlerts() {
	const alerts = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot
	);

	const createAlert = useCallback(
		(params: {
			marketSymbol: string;
			marketIndex: number;
			alertType: AlertType;
			thresholdPercent: number;
			frequency: AlertFrequency;
		}) => {
			const current = getAlerts();
			if (current.length >= MAX_FUNDING_RATE_ALERTS) return false;

			const newAlert: FundingRateAlert = {
				id: crypto.randomUUID(),
				...params,
				createdAt: Date.now(),
			};
			saveAlerts([...current, newAlert]);
			return true;
		},
		[]
	);

	const deleteAlert = useCallback((id: string) => {
		const current = getAlerts();
		saveAlerts(current.filter((a) => a.id !== id));
	}, []);

	return {
		alerts,
		createAlert,
		deleteAlert,
		canCreate: alerts.length < MAX_FUNDING_RATE_ALERTS,
	};
}
